import React from 'react';

export type ModuleArtKey =
  | 'overview'
  | 'accounting'
  | 'customers'
  | 'finance'
  | 'loans'
  | 'emi'
  | 'payments'
  | 'overdue'
  | 'accounts'
  | 'report'
  | 'audit'
  | 'erp'
  | 'consultancy'
  | 'expenses'
  | 'employees'
  | 'ledgers'
  | 'users'
  | 'settings';

interface ModuleArtProps {
  moduleKey: ModuleArtKey;
  className?: string;
}

/** Compact image-style icons (48×48) — same footprint as standard UI icons. */
export function ModuleArt({ moduleKey, className = '' }: ModuleArtProps) {
  switch (moduleKey) {
    case 'overview':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-ov" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#38bdf8" />
              <stop offset="100%" stopColor="#0284c7" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-ov)" />
          <rect x="8" y="10" width="14" height="10" rx="2" fill="#fff" opacity="0.9" />
          <rect x="8" y="24" width="14" height="14" rx="2" fill="#fff" opacity="0.75" />
          <rect x="26" y="10" width="14" height="6" rx="2" fill="#fff" opacity="0.85" />
          <path d="M26 22 H40 M26 28 H36 M26 34 H38" stroke="#fff" strokeWidth="2" strokeLinecap="round" opacity="0.7" />
        </svg>
      );
    case 'accounting':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-ac" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#34d399" />
              <stop offset="100%" stopColor="#059669" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-ac)" />
          <rect x="10" y="28" width="5" height="10" rx="1" fill="#fff" opacity="0.9" />
          <rect x="18" y="22" width="5" height="16" rx="1" fill="#fff" opacity="0.85" />
          <rect x="26" y="16" width="5" height="22" rx="1" fill="#fff" />
          <rect x="34" y="20" width="5" height="18" rx="1" fill="#fff" opacity="0.8" />
        </svg>
      );
    case 'customers':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-cu" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#a78bfa" />
              <stop offset="100%" stopColor="#7c3aed" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-cu)" />
          <circle cx="24" cy="18" r="7" fill="#fff" opacity="0.95" />
          <ellipse cx="24" cy="34" rx="12" ry="8" fill="#fff" opacity="0.85" />
        </svg>
      );
    case 'finance':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-fi" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#4ade80" />
              <stop offset="100%" stopColor="#16a34a" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-fi)" />
          <rect x="10" y="14" width="28" height="20" rx="4" fill="#fff" opacity="0.95" />
          <rect x="10" y="22" width="28" height="6" fill="#15803d" opacity="0.35" />
          <circle cx="34" cy="30" r="5" fill="#fef08a" />
        </svg>
      );
    case 'loans':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-lo" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#2dd4bf" />
              <stop offset="100%" stopColor="#0d9488" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-lo)" />
          <circle cx="24" cy="24" r="12" fill="#fff" opacity="0.2" />
          <text x="24" y="30" textAnchor="middle" fill="#fff" fontSize="18" fontWeight="700">₹</text>
          <path d="M32 14 L38 20 L32 26" stroke="#fff" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.8" />
        </svg>
      );
    case 'emi':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-em" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#818cf8" />
              <stop offset="100%" stopColor="#4f46e5" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-em)" />
          <rect x="10" y="12" width="28" height="24" rx="4" fill="#fff" opacity="0.92" />
          <rect x="14" y="18" width="8" height="8" rx="2" fill="#6366f1" />
          <rect x="24" y="18" width="8" height="8" rx="2" fill="#c7d2fe" />
          <rect x="14" y="28" width="8" height="4" rx="1" fill="#a5b4fc" />
          <rect x="24" y="28" width="8" height="4" rx="1" fill="#a5b4fc" />
        </svg>
      );
    case 'payments':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-pa" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#86efac" />
              <stop offset="100%" stopColor="#22c55e" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-pa)" />
          <circle cx="24" cy="24" r="14" fill="#fff" opacity="0.25" />
          <path d="M16 24 L22 30 L34 18" stroke="#fff" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'overdue':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-od" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#fb7185" />
              <stop offset="100%" stopColor="#e11d48" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-od)" />
          <circle cx="24" cy="24" r="14" fill="#fff" opacity="0.2" />
          <text x="24" y="31" textAnchor="middle" fill="#fff" fontSize="22" fontWeight="800">!</text>
        </svg>
      );
    case 'accounts':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-acc" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#fbbf24" />
              <stop offset="100%" stopColor="#d97706" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-acc)" />
          <rect x="12" y="14" width="10" height="20" rx="2" fill="#fff" opacity="0.9" />
          <rect x="26" y="14" width="10" height="20" rx="2" fill="#fff" opacity="0.75" />
          <rect x="14" y="18" width="6" height="4" rx="1" fill="#f59e0b" opacity="0.5" />
          <rect x="28" y="18" width="6" height="4" rx="1" fill="#0284c7" opacity="0.5" />
        </svg>
      );
    case 'report':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-rp" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#a1a1aa" />
              <stop offset="100%" stopColor="#52525b" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-rp)" />
          <rect x="12" y="10" width="24" height="28" rx="3" fill="#fff" opacity="0.92" />
          <rect x="16" y="26" width="4" height="8" rx="1" fill="#71717a" />
          <rect x="22" y="20" width="4" height="14" rx="1" fill="#52525b" />
          <rect x="28" y="16" width="4" height="18" rx="1" fill="#3f3f46" />
        </svg>
      );
    case 'audit':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-au" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#c084fc" />
              <stop offset="100%" stopColor="#9333ea" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-au)" />
          <path d="M24 8 L36 14 V24 C36 32 24 38 24 38 C24 38 12 32 12 24 V14 Z" fill="#fff" opacity="0.9" />
          <path d="M18 24 L22 28 L30 20" stroke="#7c3aed" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'erp':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-erp" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#22d3ee" />
              <stop offset="100%" stopColor="#0891b2" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-erp)" />
          <rect x="8" y="12" width="32" height="24" rx="3" fill="#fff" opacity="0.92" />
          <rect x="12" y="18" width="8" height="12" rx="1" fill="#06b6d4" opacity="0.5" />
          <rect x="22" y="18" width="8" height="12" rx="1" fill="#0891b2" opacity="0.5" />
          <rect x="32" y="18" width="4" height="12" rx="1" fill="#0e7490" opacity="0.5" />
        </svg>
      );
    case 'consultancy':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-co" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#fb923c" />
              <stop offset="100%" stopColor="#ea580c" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-co)" />
          <rect x="10" y="22" width="28" height="12" rx="3" fill="#fff" opacity="0.9" />
          <rect x="14" y="18" width="20" height="8" rx="2" fill="#fff" opacity="0.75" />
          <circle cx="16" cy="32" r="3" fill="#1e293b" opacity="0.7" />
          <circle cx="32" cy="32" r="3" fill="#1e293b" opacity="0.7" />
        </svg>
      );
    case 'expenses':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-ex" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#fde047" />
              <stop offset="100%" stopColor="#ca8a04" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-ex)" />
          <rect x="12" y="10" width="24" height="28" rx="3" fill="#fff" opacity="0.92" />
          <rect x="16" y="16" width="16" height="3" rx="1" fill="#eab308" opacity="0.6" />
          <rect x="16" y="22" width="12" height="2" rx="1" fill="#a1a1aa" />
          <rect x="16" y="27" width="14" height="2" rx="1" fill="#a1a1aa" />
          <rect x="16" y="32" width="10" height="2" rx="1" fill="#a1a1aa" />
        </svg>
      );
    case 'employees':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-emp" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#60a5fa" />
              <stop offset="100%" stopColor="#2563eb" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-emp)" />
          <circle cx="20" cy="20" r="6" fill="#fff" opacity="0.95" />
          <ellipse cx="20" cy="32" rx="9" ry="6" fill="#fff" opacity="0.85" />
          <rect x="28" y="16" width="12" height="16" rx="2" fill="#fff" opacity="0.7" />
        </svg>
      );
    case 'ledgers':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-ld" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#a8a29e" />
              <stop offset="100%" stopColor="#57534e" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-ld)" />
          <rect x="14" y="10" width="10" height="28" rx="2" fill="#fff" opacity="0.9" />
          <rect x="26" y="10" width="10" height="28" rx="2" fill="#fff" opacity="0.7" />
          <rect x="16" y="14" width="6" height="2" rx="1" fill="#78716c" />
          <rect x="28" y="14" width="6" height="2" rx="1" fill="#78716c" />
        </svg>
      );
    case 'users':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-us" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#e879f9" />
              <stop offset="100%" stopColor="#c026d3" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-us)" />
          <circle cx="18" cy="20" r="5" fill="#fff" opacity="0.95" />
          <ellipse cx="18" cy="30" rx="7" ry="5" fill="#fff" opacity="0.8" />
          <circle cx="32" cy="22" r="4" fill="#fff" opacity="0.85" />
          <ellipse cx="32" cy="30" rx="5" ry="4" fill="#fff" opacity="0.7" />
        </svg>
      );
    case 'settings':
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <defs>
            <linearGradient id="ic-st" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#d4d4d8" />
              <stop offset="100%" stopColor="#71717a" />
            </linearGradient>
          </defs>
          <rect width="48" height="48" rx="12" fill="url(#ic-st)" />
          <circle cx="24" cy="24" r="10" fill="#fff" opacity="0.25" />
          <circle cx="24" cy="24" r="5" fill="#fff" opacity="0.9" />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
            <rect
              key={deg}
              x="22"
              y="10"
              width="4"
              height="7"
              rx="1"
              fill="#fff"
              opacity="0.85"
              transform={`rotate(${deg} 24 24)`}
            />
          ))}
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden>
          <rect width="48" height="48" rx="12" fill="#d4d4d8" />
        </svg>
      );
  }
}
